import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import CTAButton from '../../../../CTAButton';
import OcLabel from '../../../../ComponentLibrary/input-elements/oc-label';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import { QuestionWrapper, SubQuestionWrapper } from '../../styles';
import generateResultsPdf from '../WallsAboveGrade/pdfResults';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import {
  validateInputs,
  resetResults,
  roundOff,
  CODEBORD_RSI_PER_INCH,
  CODEBORD_THICKNESS_OPTIONS,
  RAINBARRIER_THICKNESS_OPTIONS,
} from '../shared/constants';
import { DropdownField, Section, YesNo } from '../shared/formControls';
import { smoothScrollIntoView } from '../shared/scroll';

const AIR_FILM_INTERIOR_RSI = 0.16;
const RAINBARRIER_RSI_PER_INCH = 0.73;

const CONCRETE_RSI_BY_MM = {
  102: 0.0408,
  152: 0.0608,
  203: 0.0812,
};

const CONCRETE_FLOOR_OPTIONS = [
  { value: '102', text: '102mm (4")' },
  { value: '152', text: '152mm (6")' },
  { value: '203', text: '203mm (8")' },
];

// —— defaults for this calc’s inputs
const DEFAULTS = {
  concreteFloorThickness: '102',
  isFloorInsulated: true,
  floorInsulationType: 'codebord',
  codebordThickness: '',
  rainbarrierThickness: '',
  hasVapourBarrier: true,
};

const ConcreteFoundationFloor = ({ calcId, t }) => {
  // ---- read location from the store
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);

  // —— hydrate local state from store (if present), else use defaults
  const savedInputs = state.inputsByCalc?.[calcId];

  const [concreteFloorThickness, setConcreteFloorThickness] = useState(
    savedInputs?.concreteFloorThickness ?? DEFAULTS.concreteFloorThickness
  );

  const [isFloorInsulated, setIsFloorInsulated] = useState(
    savedInputs?.hasSheathingMaterial ?? DEFAULTS.isFloorInsulated
  );

  const [floorInsulationType, setFloorInsulationType] = useState(
    savedInputs?.sheathingMaterialType ?? DEFAULTS.floorInsulationType
  );

  const [codebordThickness, setCodebordThickness] = useState(
    savedInputs?.codebordThickness ?? DEFAULTS.codebordThickness
  );

  // eslint-disable-next-line max-len
  const [rainbarrierThickness, setRainbarrierThickness] = useState(
    savedInputs?.rainbarrierThickness ?? DEFAULTS.rainbarrierThickness
  );

  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs?.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [error]);

  // —— whenever inputs change, publish them to the store so tab switches persist
  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        concreteFloorThickness,
        isFloorInsulated,
        floorInsulationType,
        codebordThickness,
        rainbarrierThickness,
        hasVapourBarrier,
      },
    });
  }, [
    calcId,
    concreteFloorThickness,
    isFloorInsulated,
    floorInsulationType,
    codebordThickness,
    rainbarrierThickness,
    hasVapourBarrier,
    publishInputs,
  ]);

  const results = state.resultsByCalc?.[calcId]?.results || null;

  const rowsForTable = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI', roundR: true },
  ];

  // ---- calculator
  const calculate = () => {
    const inches = v => parseFloat(String(v || '0'));
    const calcFloorInsulationRSI = () => {
      if (!isFloorInsulated) return 0;
      if (floorInsulationType === 'codebord') return CODEBORD_RSI_PER_INCH * inches(codebordThickness || '0');
      if (floorInsulationType === 'rainbarrier') return RAINBARRIER_RSI_PER_INCH * inches(rainbarrierThickness || '0');
      return 0;
    };

    const concreteFloorRSI = CONCRETE_RSI_BY_MM[concreteFloorThickness] || 0;
    const vapourBarrierRSI = hasVapourBarrier ? 0 : 0; // explicit 0 (kept in ledger, colored differently in PDF)
    const floorInsulationRSI = roundOff(calcFloorInsulationRSI(), 2);
    // ------- Continuous sum (foundation stack) -------
    const continuousTotalRSI = concreteFloorRSI + floorInsulationRSI + AIR_FILM_INTERIOR_RSI + vapourBarrierRSI;

    // ------- Outputs -------
    const effectiveAssemblyRSI = continuousTotalRSI;
    const installedInsulationRSI = floorInsulationRSI || 0;

    // ------- PDF/Results ledger -------
    const concreteSlabText = CONCRETE_FLOOR_OPTIONS.find(opt => opt.value === concreteFloorThickness).text;
    const insulationThickness = floorInsulationType === 'codebord' ? codebordThickness : rainbarrierThickness;
    const insulationText = CODEBORD_THICKNESS_OPTIONS.find(opt => opt.value === insulationThickness)?.text;

    const layers = {
      continuous: [
        {
          id: 'film_int',
          label: t('thermal_calculator.concrete_foundation_floor.film_int'),
          rsi: AIR_FILM_INTERIOR_RSI,
        },
        { id: 'concrete_slab', label: concreteSlabText, rsi: AIR_FILM_INTERIOR_RSI },
        ...(isFloorInsulated
          ? [
              {
                id: 'floor_insulation',
                label:
                  floorInsulationType === 'codebord'
                    ? `${t('thermal_calculator.floor_insulation')} (Codebord): ${insulationText}"`
                    : `${t('thermal_calculator.floor_insulation')} (Rainbarrier): ${insulationText}"`,
                rsi: floorInsulationRSI,
                highlight: true,
              },
            ]
          : []),
        {
          id: 'vb',
          label: t('thermal_calculator.concrete_foundation_floor.vapour_barrier_rsi'),
          rsi: vapourBarrierRSI,
        },
      ],
    };

    return {
      continuousTotalRSI,
      effectiveAssemblyRSI,
      installedInsulationRSI,
      layers,
    };
  };

  const onCalculate = () => {
    if (!validateInputs({ province, region, setError, cavityRSI: '', validateCavityRSI: false, t })) {
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      // do not change persisted inputs; just report error
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    // perform calculation
    const res = calculate();
    publishResults({
      calcId,
      results: res,
      meta: { title: t('thermal_calculator.titles.concrete_foundation_floor') },
    });
    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    resetResults(calcId, t('thermal_calculator.titles.concrete_foundation_floor'), publishResults);
    // reset local inputs to defaults
    setConcreteFloorThickness(DEFAULTS.concreteFloorThickness);
    setIsFloorInsulated(DEFAULTS.isFloorInsulated);
    setFloorInsulationType(DEFAULTS.floorInsulationType);
    setCodebordThickness(DEFAULTS.codebordThickness);
    setRainbarrierThickness(DEFAULTS.rainbarrierThickness);
    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);
    setError(null);
    // clear inputs in store
    publishInputs({ calcId, inputs: null });
  };

  // ---- sidebar UI
  const sidebar = (
    <Section title={t('thermal_calculator.continuous_layers')}>
      <QuestionWrapper>
        <OcLabel text={t('thermal_calculator.concrete_foundation_floor.what_floor_thickness')} />
        <DropdownField
          id={`concrete-floor-${calcId}`}
          value={concreteFloorThickness}
          onChange={e => setConcreteFloorThickness(e.target.value)}
          options={CONCRETE_FLOOR_OPTIONS}
        />
      </QuestionWrapper>

      <QuestionWrapper>
        <OcLabel text={t('thermal_calculator.concrete_foundation_floor.concrete_floor_insulated')} />
        <YesNo
          name={`floor-insulation-${calcId}`}
          value={isFloorInsulated}
          onChange={val => {
            setIsFloorInsulated(val);
          }}
        />
      </QuestionWrapper>

      {isFloorInsulated && (
        <div>
          <QuestionWrapper>
            <OcLabel text={t('thermal_calculator.concrete_foundation_floor.floor_insulation_type')} />
            <DropdownField
              id={`sheathing-type-${calcId}`}
              value={floorInsulationType}
              onChange={e => setFloorInsulationType(e.target.value)}
              options={[
                { value: 'codebord', text: t('thermal_calculator.products.codebord') },
                { value: 'rainbarrier', text: t('thermal_calculator.products.rainbarrier_ci_110') },
              ]}
            />
          </QuestionWrapper>

          {floorInsulationType === 'codebord' ? (
            <SubQuestionWrapper>
              <OcLabel text={t('thermal_calculator.concrete_foundation_floor.codebord_thickness')} />
              <DropdownField
                id={`codebord-${calcId}`}
                value={codebordThickness}
                onChange={e => setCodebordThickness(e.target.value)}
                options={CODEBORD_THICKNESS_OPTIONS}
              />
            </SubQuestionWrapper>
          ) : (
            <SubQuestionWrapper>
              <OcLabel text={t('thermal_calculator.concrete_foundation_floor.rainbarrier_thickness')} />
              <DropdownField
                id={`rainbarrier-${calcId}`}
                value={rainbarrierThickness}
                onChange={e => setRainbarrierThickness(e.target.value)}
                options={RAINBARRIER_THICKNESS_OPTIONS}
              />
            </SubQuestionWrapper>
          )}
        </div>
      )}

      <QuestionWrapper>
        <OcLabel text={t('thermal_calculator.form.vapour_barrier_winter')} />
        <YesNo
          name={`vapour-barrier-${calcId}`}
          value={hasVapourBarrier}
          onChange={val => {
            setHasVapourBarrier(val);
          }}
        />
      </QuestionWrapper>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <CTAButton
          onClick={onCalculate}
          disabled={!hasLocation}
          text={t('thermal_calculator.calculate_r_value')}
          style={{ minWidth: '200px' }}
        />
        <CTAButton
          element="button"
          type="button"
          onClick={onReset}
          text={t('thermal_calculator.reset')}
          style={{
            minWidth: '100px',
            padding: '8px 16px',
            border: '1px solid #ccc',
            background: '#fff',
            color: '#000',
          }}
        />
      </div>
    </Section>
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      projectLink: 'https://insulation.owenscorning.ca/builders/calculators/thermal-project-calculator/',
      assemblyHeading: t('thermal_calculator.titles.concrete_foundation_floor'),
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig: rowsForTable,
      results,
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />
      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

ConcreteFoundationFloor.propTypes = {
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connectLocation(ConcreteFoundationFloor);
